import { useEffect, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { useAuthStore } from "@/store/auth";
import { useBranchesList, useCreateAsset, useUpdateAsset } from "../hooks/useAssetRegistry";
import { VISIBILITY_LABELS, type Asset, type AssetVisibility } from "../types";
import { extractError } from "../utils";

interface Props {
  open: boolean;
  onClose: () => void;
  asset?: Asset | null;
}

export function AssetFormModal({ open, onClose, asset }: Props) {
  const user = useAuthStore((s) => s.user);
  const isCeo = !!user?.is_ceo;
  const isEdit = !!asset;
  // Only a CEO picks the branch (on create). A director's asset is auto-assigned to
  // their own branch by the backend, so no branch field is shown for them.
  const showBranchPicker = isCeo && !isEdit;

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [visibility, setVisibility] = useState<AssetVisibility>("all");
  const [branch, setBranch] = useState("");
  const [error, setError] = useState("");

  const createAsset = useCreateAsset();
  const updateAsset = useUpdateAsset();
  const { data: branches = [], isLoading: branchesLoading } = useBranchesList(open && showBranchPicker);

  useEffect(() => {
    if (open) {
      setName(asset?.name ?? "");
      setDescription(asset?.description ?? "");
      setVisibility(asset?.visibility ?? "all");
      setBranch("");
      setError("");
    }
  }, [open, asset]);

  function submit() {
    if (!name.trim()) {
      setError("Podaj nazwę obiektu.");
      return;
    }
    if (showBranchPicker && !branch) {
      setError("Wskaż oddział.");
      return;
    }
    const payload: { name: string; description: string; visibility: string; branch?: string } = {
      name: name.trim(),
      description: description.trim(),
      visibility,
    };
    if (showBranchPicker) payload.branch = branch;
    const onSuccess = () => onClose();
    const onError = (e: unknown) => setError(extractError(e));
    if (isEdit && asset) {
      // Branch is never editable here (only via transfer) — send just the editable fields.
      updateAsset.mutate(
        { id: asset.id, data: { name: payload.name, description: payload.description, visibility } },
        { onSuccess, onError },
      );
    } else {
      createAsset.mutate(payload, { onSuccess, onError });
    }
  }

  const pending = createAsset.isPending || updateAsset.isPending;

  return (
    <Modal open={open} onClose={onClose} title={isEdit ? "Edytuj obiekt" : "Nowy obiekt"}>
      <div className="space-y-4">
        <div>
          <label className="block text-xs font-mono uppercase tracking-wider text-[var(--color-text-muted)] mb-1">
            Nazwa *
          </label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-3 py-2 text-sm bg-[var(--color-bg-elevated)] border border-[var(--color-border)] text-[var(--color-text-primary)]"
            placeholder="np. Laptop Dell Latitude 5540"
          />
        </div>
        <div>
          <label className="block text-xs font-mono uppercase tracking-wider text-[var(--color-text-muted)] mb-1">
            Opis
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 text-sm bg-[var(--color-bg-elevated)] border border-[var(--color-border)] text-[var(--color-text-primary)]"
            placeholder="Numer seryjny, specyfikacja, uwagi…"
          />
        </div>
        {showBranchPicker && (
          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-[var(--color-text-muted)] mb-1">
              Oddział *
            </label>
            <select
              value={branch}
              onChange={(e) => setBranch(e.target.value)}
              disabled={branchesLoading}
              className="w-full px-3 py-2 text-sm bg-[var(--color-bg-elevated)] border border-[var(--color-border)] text-[var(--color-text-primary)]"
            >
              <option value="">{branchesLoading ? "Ładowanie…" : "— wybierz —"}</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>
        )}
        <div>
          <label className="block text-xs font-mono uppercase tracking-wider text-[var(--color-text-muted)] mb-1">
            Widoczność
          </label>
          <select
            value={visibility}
            onChange={(e) => setVisibility(e.target.value as AssetVisibility)}
            className="w-full px-3 py-2 text-sm bg-[var(--color-bg-elevated)] border border-[var(--color-border)] text-[var(--color-text-primary)]"
          >
            <option value="all">{VISIBILITY_LABELS.all}</option>
            <option value="holder">{VISIBILITY_LABELS.holder}</option>
          </select>
        </div>
        {error && <p className="text-xs text-red-600">{error}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>
            Anuluj
          </Button>
          <Button onClick={submit} loading={pending}>
            {isEdit ? "Zapisz" : "Dodaj"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
